import { HashRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { ThemeProvider } from '@/context/ThemeContext'
import { AppProvider, useApp } from '@/context/AppContext'
import { isAuthenticated } from '@/services/authService'
import { loadAggregatedData } from '@/services/aggregatedDataLoader'
import { useTribeDataLoader } from '@/hooks/useTribeDataLoader'
import { Header } from '@/components/layout/Header'
import { Sidebar } from '@/components/layout/Sidebar'
import { Login } from '@/pages/Login'
import { Welcome } from '@/pages/Welcome'
import { HowToNavigate } from '@/pages/HowToNavigate'
import { Identity } from '@/pages/Identity'
import { IdentityStatements } from '@/pages/IdentityStatements'
import { FutureThreats } from '@/pages/FutureThreats'
import { BeliefsAndDoubts } from '@/pages/BeliefsAndDoubts'
import { ValuesAndIssues } from '@/pages/ValuesAndIssues'
import { ActivitiesInParks } from '@/pages/ActivitiesInParks'
import { ConfidenceInSources } from '@/pages/ConfidenceInSources'
import { FederalLeaders } from '@/pages/FederalLeaders'
import { ProtectedAreas } from '@/pages/ProtectedAreas'
import { DemographicsGeneral } from '@/pages/DemographicsGeneral'
import { DemographicsDetails } from '@/pages/DemographicsDetails'
import { PartySupport } from '@/pages/PartySupport'
import { Scatterplot } from '@/pages/Scatterplot'
import { QuestionComparison } from '@/pages/QuestionComparison'
import { ReasonsToCreate } from '@/pages/ReasonsToCreate'
import { ReasonsNotToCreate } from '@/pages/ReasonsNotToCreate'
import { MostLeast } from '@/pages/MostLeast'
import { IndigenousProtectedAreas } from '@/pages/IndigenousProtectedAreas'
import { ProvincialLeaders } from '@/pages/ProvincialLeaders'
import { SupportMoreParks } from '@/pages/SupportMoreParks'
import { SupportIPAsAndParks } from '@/pages/SupportIPAsAndParks'
import { HowMuchProtected } from '@/pages/HowMuchProtected'
import { EnvironmentalConcerns } from '@/pages/EnvironmentalConcerns'
import { OpenEndedResponses } from '@/pages/OpenEndedResponses'
import { PairedStatements } from '@/pages/PairedStatements'
// TEMP DISABLED: import { CoalitionAnalysis } from '@/pages/CoalitionAnalysis'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

// Protected route wrapper - redirects to login if not authenticated
function ProtectedRoute() {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />
  }
  return <Outlet />
}

// Main dashboard layout with header and sidebar
function DashboardLayout() {
  const { dispatch } = useApp()

  // Load tribe-specific data when tribe filter changes
  useTribeDataLoader()

  // Update current dashboard based on route
  useEffect(() => {
    const updateCurrentDashboard = () => {
      const hash = window.location.hash.replace('#/', '').replace('/', '')
      const formatted = hash
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      dispatch({ type: 'SET_CURRENT_DASHBOARD', payload: formatted || 'Welcome' })
    }

    updateCurrentDashboard()
    window.addEventListener('hashchange', updateCurrentDashboard)
    return () => window.removeEventListener('hashchange', updateCurrentDashboard)
  }, [dispatch])

  return (
    <div className="min-h-screen min-w-[1280px] bg-slate-50 dark:bg-slate-900">
      <Header />
      <div className="flex">
        <Sidebar />
        <main id="dashboard-content" className="flex-1 p-6 overflow-auto h-[calc(100vh-3.5rem)]">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  )
}

// App initialization and data loading
function AppContent() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { dispatch } = useApp()

  useEffect(() => {
    async function initializeApp() {
      try {
        const result = await loadAggregatedData()
        dispatch({ type: 'SET_DATA', payload: result.data })
        setLoading(false)
      } catch (err) {
        console.error('Failed to load data:', err)
        setError('Failed to load dashboard data. Please refresh the page.')
        setLoading(false)
      }
    }

    initializeApp()
  }, [dispatch])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center p-8">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Data Load Error
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <HashRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<DashboardLayout />}>
            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/welcome" replace />} />

            {/* Getting Started */}
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/how-to-navigate" element={<HowToNavigate />} />

            {/* Activities */}
            <Route path="/activities-in-parks" element={<ActivitiesInParks />} />

            {/* Values & Beliefs */}
            <Route path="/beliefs-and-doubts" element={<BeliefsAndDoubts />} />
            <Route path="/values-and-issues" element={<ValuesAndIssues />} />
            <Route path="/open-ended-responses" element={<OpenEndedResponses />} />
            <Route path="/paired-statements" element={<PairedStatements />} />

            {/* Confidence */}
            <Route path="/confidence-in-sources" element={<ConfidenceInSources />} />

            {/* Demographics */}
            <Route path="/demographics-general" element={<DemographicsGeneral />} />
            <Route path="/demographics-details" element={<DemographicsDetails />} />

            {/* Political */}
            <Route path="/party-support" element={<PartySupport />} />
            <Route path="/federal-leaders" element={<FederalLeaders />} />
            <Route path="/provincial-leaders" element={<ProvincialLeaders />} />

            {/* Threats & Concerns */}
            <Route path="/future-threats" element={<FutureThreats />} />
            <Route path="/environmental-concerns" element={<EnvironmentalConcerns />} />

            {/* Identity */}
            <Route path="/identity" element={<Identity />} />
            <Route path="/identity-statements" element={<IdentityStatements />} />
            <Route path="/most-least" element={<MostLeast />} />

            {/* Protected Areas */}
            <Route path="/protected-areas" element={<ProtectedAreas />} />
            <Route path="/support-more-parks" element={<SupportMoreParks />} />
            <Route path="/reasons-to-create" element={<ReasonsToCreate />} />
            <Route path="/reasons-not-to-create" element={<ReasonsNotToCreate />} />
            <Route path="/support-ipas-and-parks" element={<SupportIPAsAndParks />} />
            <Route path="/indigenous-protected-areas" element={<IndigenousProtectedAreas />} />
            <Route path="/how-much-protected" element={<HowMuchProtected />} />

            {/* Analysis */}
            <Route path="/scatterplot" element={<Scatterplot />} />
            <Route path="/question-comparison" element={<QuestionComparison />} />
            {/* TEMP DISABLED: <Route path="/coalition" element={<CoalitionAnalysis />} /> */}

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/welcome" replace />} />
          </Route>
        </Route>
      </Routes>
    </HashRouter>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ThemeProvider>
  )
}

export default App
